/**
 * Renders the entire project as a single printable, magazine-style document.
 * Used (visually hidden / off-screen) by html2canvas + jsPDF for full-plan export.
 */
import { useLang } from "@/contexts/LanguageContext";
import {
  computeBreakEven,
  computePL,
  computeBalance,
  computeFiveYears,
  computeMonthlyProjection,
  year1Totals,
} from "@/lib/finance";

const M = (data) => data || {};

function H1({ children }) {
  return (
    <h1
      className="text-5xl tracking-tight text-[#1A1A1A] leading-none"
      style={{ fontFamily: '"Playfair Display", serif', fontWeight: 700 }}
    >
      {children}
    </h1>
  );
}
function H2({ children, num }) {
  return (
    <div className="border-t-2 border-[#2563EB] pt-6 mt-12">
      {num && (
        <div className="text-xs uppercase tracking-[0.3em] text-[#B89154] font-mono mb-2" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
          {num}
        </div>
      )}
      <h2
        className="text-3xl tracking-tight text-[#1A1A1A]"
        style={{ fontFamily: '"Playfair Display", serif', fontWeight: 600 }}
      >
        {children}
      </h2>
    </div>
  );
}
function H3({ children }) {
  return (
    <h3
      className="text-xl text-[#2563EB] mt-6 mb-3"
      style={{ fontFamily: '"Playfair Display", serif', fontWeight: 600 }}
    >
      {children}
    </h3>
  );
}
function P({ children, muted }) {
  return (
    <p className={`text-sm leading-relaxed ${muted ? "text-[#5C5B5A]" : "text-[#1A1A1A]"}`}>
      {children || "—"}
    </p>
  );
}
function KV({ k, v }) {
  return (
    <div className="grid grid-cols-[180px_1fr] gap-3 py-1 border-b border-[#F0EFE9]">
      <div className="text-xs uppercase tracking-wide text-[#8A8886]">{k}</div>
      <div className="text-sm text-[#1A1A1A]">{v || "—"}</div>
    </div>
  );
}

export default function FullPlanDocument({ project }) {
  const { t, lang } = useLang();
  const m = project.modules || {};
  const concept = M(m.concept);
  const market = M(m.market);
  const ops = M(m.operations);
  const hr = M(m.hr);
  const cal = M(m.calendar);
  const mk = M(m.marketing);
  const bg = M(m.budget);
  const risks = M(m.risks);
  const exec = M(m.executive_summary);
  const canvas = M(m.canvas);
  const gya = M(m.gya);

  return (
    <div className="bg-white p-16" style={{ width: 800, fontFamily: 'Manrope, sans-serif', color: "#1A1A1A" }}>
      {/* Cover */}
      <div className="min-h-[700px] flex flex-col justify-between border-b border-[#E8E6E1] pb-10">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[#B89154] font-mono" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
            {lang === "es" ? "Plan de Negocio" : "Business Plan"}
          </div>
          <div className="mt-32">
            <H1>{project.name}</H1>
            {project.description && (
              <p className="mt-6 text-lg text-[#5C5B5A] max-w-xl" style={{ fontFamily: '"Playfair Display", serif', fontStyle: 'italic' }}>
                {project.description}
              </p>
            )}
          </div>
        </div>
        <div className="text-xs uppercase tracking-[0.2em] text-[#8A8886] font-mono" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
          {new Date().toLocaleDateString(lang === "es" ? "es-ES" : "en-US", { day: "2-digit", month: "long", year: "numeric" })}
          <span className="mx-3">·</span>
          Atelier Business
        </div>
      </div>

      {/* 01 — Executive Summary */}
      <H2 num="01">{t("module.executive_summary")}</H2>
      <div className="mt-4 whitespace-pre-wrap text-sm leading-relaxed text-[#1A1A1A]">
        {exec.summary || (lang === "es" ? "(No generado todavía)" : "(Not generated yet)")}
      </div>

      {/* 01b — Business Model Canvas */}
      <H2 num="02">{t("module.canvas")}</H2>
      <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
        {[
          ["key_partners", t("canvas.key_partners")],
          ["key_activities", t("canvas.key_activities")],
          ["value_propositions", t("canvas.value_propositions")],
          ["customer_relationships", t("canvas.customer_relationships")],
          ["customer_segments", t("canvas.customer_segments")],
          ["key_resources", t("canvas.key_resources")],
          ["channels", t("canvas.channels")],
          ["cost_structure", t("canvas.cost_structure")],
          ["revenue_streams", t("canvas.revenue_streams")],
        ].map(([k, label]) => (
          <div key={k} className="border border-[#E8E6E1] p-3 bg-white">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#B89154] mb-2">{label}</div>
            <div className="text-[#1A1A1A] leading-snug whitespace-pre-wrap">{canvas[k] || "—"}</div>
          </div>
        ))}
      </div>

      {/* 03 — Concept */}
      <H2 num="03">{t("module.concept")}</H2>
      <KV k={t("concept.company_name")} v={concept.company_name} />
      <KV k={t("concept.legal_form")} v={concept.legal_form} />
      <KV k={t("concept.location")} v={concept.location} />
      <H3>{t("concept.idea")}</H3>
      <P>{concept.idea}</P>
      <H3>{t("concept.differentiators")}</H3>
      <P>{concept.differentiators}</P>
      <H3>{t("concept.competitive_advantage")}</H3>
      <P>{concept.competitive_advantage}</P>
      <H3>{lang === "es" ? "Misión · Visión · Valores" : "Mission · Vision · Values"}</H3>
      <div className="grid grid-cols-3 gap-4 text-sm">
        <div><div className="text-xs uppercase text-[#8A8886] mb-1">{t("concept.mission")}</div>{concept.mission || "—"}</div>
        <div><div className="text-xs uppercase text-[#8A8886] mb-1">{t("concept.vision")}</div>{concept.vision || "—"}</div>
        <div><div className="text-xs uppercase text-[#8A8886] mb-1">{t("concept.values")}</div>{concept.values || "—"}</div>
      </div>
      {(concept.partners || []).length > 0 && (
        <>
          <H3>{t("concept.partners")}</H3>
          {concept.partners.map((p, i) => (
            <KV key={i} k={p.role || "—"} v={`${p.name} — ${p.equity}%`} />
          ))}
        </>
      )}

      {/* 04 — Market */}
      <H2 num="04">{t("module.market")}</H2>
      <H3>{t("market.size")}</H3>
      <P>{market.market_size}</P>
      <H3>{t("market.opportunity")}</H3>
      <P>{market.opportunity}</P>
      <H3>{t("market.client_def")}</H3>
      <P>{market.client_definition}</P>
      {market.swot && (
        <>
          <H3>{t("market.swot")}</H3>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="bg-[#F5F4F0] p-3 rounded-sm">
              <div className="text-xs uppercase text-[#2563EB] mb-1">{t("swot.strengths")}</div>{market.swot.strengths || "—"}
            </div>
            <div className="bg-[#F5F4F0] p-3 rounded-sm">
              <div className="text-xs uppercase text-[#9E2A2B] mb-1">{t("swot.weaknesses")}</div>{market.swot.weaknesses || "—"}
            </div>
            <div className="bg-[#F5F4F0] p-3 rounded-sm">
              <div className="text-xs uppercase text-[#B89154] mb-1">{t("swot.opportunities")}</div>{market.swot.opportunities || "—"}
            </div>
            <div className="bg-[#F5F4F0] p-3 rounded-sm">
              <div className="text-xs uppercase text-[#3A5A6B] mb-1">{t("swot.threats")}</div>{market.swot.threats || "—"}
            </div>
          </div>
        </>
      )}
      {(market.competitors || []).length > 0 && (
        <>
          <H3>{t("market.competitors")}</H3>
          {market.competitors.map((c, i) => (
            <div key={i} className="text-sm mb-2 border-b border-[#F0EFE9] pb-2">
              <div className="font-semibold">{c.name}</div>
              <div className="text-[#5C5B5A]">{c.description}</div>
            </div>
          ))}
        </>
      )}
      {(market.buyer_personas || []).length > 0 && (
        <>
          <H3>{t("market.personas")}</H3>
          {market.buyer_personas.map((p, i) => (
            <div key={i} className="text-sm mb-3 border-b border-[#F0EFE9] pb-2">
              <div className="font-semibold">{p.name} · {p.age} · {p.role}</div>
              <div className="text-[#5C5B5A]">Goals: {p.goals} · Pains: {p.pains}</div>
            </div>
          ))}
        </>
      )}

      {/* 05 — Operations */}
      <H2 num="05">{t("module.operations")}</H2>
      <P>{ops.description}</P>
      {(ops.services || []).length > 0 && (
        <>
          <H3>{t("ops.services")}</H3>
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b-2 border-[#1A1A1A] text-left">
                <th className="py-2 text-xs uppercase tracking-wide">{t("ops.service_name")}</th>
                <th className="py-2 text-xs uppercase tracking-wide text-right">{t("ops.price")}</th>
                <th className="py-2 text-xs uppercase tracking-wide text-right">{t("ops.margin")}</th>
                <th className="py-2 text-xs uppercase tracking-wide">{t("ops.duration")}</th>
              </tr>
            </thead>
            <tbody>
              {ops.services.map((s, i) => (
                <tr key={i} className="border-b border-[#F0EFE9]">
                  <td className="py-2">{s.name}<div className="text-xs text-[#8A8886]">{s.description}</div></td>
                  <td className="py-2 text-right font-mono">€{Number(s.price).toLocaleString()}</td>
                  <td className="py-2 text-right font-mono">{s.margin}%</td>
                  <td className="py-2">{s.duration}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
      {ops.key_processes && (<><H3>{t("ops.processes")}</H3><P>{ops.key_processes}</P></>)}
      {ops.suppliers_partners && (<><H3>{t("ops.suppliers")}</H3><P>{ops.suppliers_partners}</P></>)}

      {/* 06 — HR */}
      <H2 num="06">{t("module.hr")}</H2>
      <P>{hr.needs_description}</P>
      {(hr.roles || []).length > 0 && (
        <table className="w-full text-sm border-collapse mt-3">
          <thead>
            <tr className="border-b-2 border-[#1A1A1A] text-left">
              <th className="py-2 text-xs uppercase">{t("hr.title")}</th>
              <th className="py-2 text-xs uppercase">{t("hr.salary")}</th>
              <th className="py-2 text-xs uppercase text-right">{t("hr.headcount")}</th>
            </tr>
          </thead>
          <tbody>
            {hr.roles.map((r, i) => (
              <tr key={i} className="border-b border-[#F0EFE9]">
                <td className="py-2">{r.title}<div className="text-xs text-[#8A8886]">{r.responsibilities}</div></td>
                <td className="py-2">{r.salary_range}</td>
                <td className="py-2 text-right font-mono">{r.headcount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* 07 — Calendar */}
      <H2 num="07">{t("module.calendar")}</H2>
      {(cal.annual_milestones || []).length > 0 && (
        <>
          <H3>{t("calendar.annual")}</H3>
          {cal.annual_milestones.map((mi, i) => (
            <div key={i} className="text-sm py-1 border-b border-[#F0EFE9]">
              <span className="inline-block w-12 font-mono text-[#B89154]">{mi.quarter}</span>
              <span className="font-semibold">{mi.title}</span>
              <span className="text-[#5C5B5A] ml-2">{mi.description}</span>
            </div>
          ))}
        </>
      )}
      {(cal.five_year_plan || []).length > 0 && (
        <>
          <H3>{t("calendar.five_year")}</H3>
          {cal.five_year_plan.map((y, i) => (
            <div key={i} className="text-sm py-2 border-b border-[#F0EFE9]">
              <div className="font-semibold">{y.year}</div>
              <div className="text-[#5C5B5A]">{y.goals}</div>
            </div>
          ))}
        </>
      )}

      {/* 08 — Marketing */}
      <H2 num="07">{t("module.marketing")}</H2>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <KV k={t("marketing.target_cpl")} v={`€${mk.target_cpl || 0}`} />
        <KV k={t("marketing.target_cac")} v={`€${mk.target_cac || 0}`} />
      </div>
      {(mk.b2b_actions || []).length > 0 && (
        <>
          <H3>{t("marketing.b2b")}</H3>
          {mk.b2b_actions.map((a, i) => (
            <div key={i} className="text-sm py-1 border-b border-[#F0EFE9]">
              <span className="font-semibold">{a.channel}</span>
              <span className="text-[#5C5B5A] ml-2">{a.description}</span>
              <span className="float-right font-mono">€{Number(a.budget).toLocaleString()}</span>
            </div>
          ))}
        </>
      )}
      {(mk.b2c_actions || []).length > 0 && (
        <>
          <H3>{t("marketing.b2c")}</H3>
          {mk.b2c_actions.map((a, i) => (
            <div key={i} className="text-sm py-1 border-b border-[#F0EFE9]">
              <span className="font-semibold">{a.channel}</span>
              <span className="text-[#5C5B5A] ml-2">{a.description}</span>
              <span className="float-right font-mono">€{Number(a.budget).toLocaleString()}</span>
            </div>
          ))}
        </>
      )}
      {(mk.digital_campaigns || []).length > 0 && (
        <>
          <H3>{t("marketing.digital")}</H3>
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b-2 border-[#1A1A1A] text-left">
                <th className="py-2 text-xs uppercase">{t("marketing.platform")}</th>
                <th className="py-2 text-xs uppercase text-right">{t("marketing.budget")}</th>
                <th className="py-2 text-xs uppercase text-right">{t("marketing.leads")}</th>
                <th className="py-2 text-xs uppercase text-right">{t("marketing.cpl")}</th>
                <th className="py-2 text-xs uppercase text-right">{t("marketing.cac")}</th>
              </tr>
            </thead>
            <tbody>
              {mk.digital_campaigns.map((c, i) => {
                const cpl = c.leads > 0 ? (c.budget / c.leads).toFixed(2) : "—";
                const cac = c.conversions > 0 ? (c.budget / c.conversions).toFixed(2) : "—";
                return (
                  <tr key={i} className="border-b border-[#F0EFE9]">
                    <td className="py-2">{c.platform}</td>
                    <td className="py-2 text-right font-mono">€{Number(c.budget).toLocaleString()}</td>
                    <td className="py-2 text-right font-mono">{c.leads}</td>
                    <td className="py-2 text-right font-mono">€{cpl}</td>
                    <td className="py-2 text-right font-mono">€{cac}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}

      {/* 09 — Budget */}
      <H2 num="09">{t("module.budget")}</H2>
      {(() => {
        const totalStartup = (bg.startup_costs || []).reduce((s, x) => s + (parseFloat(x.amount) || 0), 0);
        const totalFixed = (bg.fixed_monthly || []).reduce((s, x) => s + (parseFloat(x.amount) || 0), 0);
        const totalVariable = (bg.variable_monthly || []).reduce((s, x) => s + (parseFloat(x.amount) || 0), 0);
        const proj = computeMonthlyProjection({
          revenue_streams: bg.revenue_streams || [],
          totalMonthlyExpenses: totalFixed + totalVariable,
          totalStartup,
        });
        const totals = year1Totals(proj, totalFixed, totalVariable);
        const taxRate = parseFloat(bg.tax_rate) || 25;
        const pl = computePL({ revenue: totals.revenue, fixedExpenses: totals.fixedYearly, variableExpenses: totals.variableYearly, taxRate });
        const bal = computeBalance({
          initialCapital: parseFloat(bg.initial_capital) || 0,
          startupCosts: totalStartup,
          year1Revenue: totals.revenue,
          year1Expenses: totals.expenses,
          year1Tax: pl.tax,
        });
        const be = computeBreakEven({
          unitRevenue: parseFloat(bg.unit_avg_revenue) || 0,
          unitVariable: parseFloat(bg.unit_avg_variable_cost) || 0,
          totalFixedMonthly: totalFixed,
          monthlyProjection: proj,
        });
        const fy = computeFiveYears({
          y1Revenue: totals.revenue,
          y1Expenses: totals.expenses,
          taxRate,
          revenueGrowth: parseFloat(bg.yearly_revenue_growth) || 0,
          expenseGrowth: parseFloat(bg.yearly_expense_growth) || 0,
        });
        const fmt = (n) => `€${Math.round(n).toLocaleString()}`;
        return (
          <>
            {[
              ["budget.startup", bg.startup_costs],
              ["budget.fixed", bg.fixed_monthly],
              ["budget.variable", bg.variable_monthly],
            ].map(([key, list]) => {
              if (!(list || []).length) return null;
              const total = list.reduce((s, x) => s + (parseFloat(x.amount) || 0), 0);
              return (
                <div key={key} className="mt-4">
                  <H3>{t(key)}</H3>
                  {list.map((it, i) => (
                    <div key={i} className="text-sm py-1 border-b border-[#F0EFE9] flex justify-between">
                      <span>{it.item}</span>
                      <span className="font-mono">€{Number(it.amount).toLocaleString()}</span>
                    </div>
                  ))}
                  <div className="text-sm py-2 flex justify-between font-mono font-semibold border-b-2 border-[#1A1A1A]">
                    <span>Total</span><span>€{total.toLocaleString()}</span>
                  </div>
                </div>
              );
            })}
            {(bg.revenue_streams || []).length > 0 && (
              <>
                <H3>{t("budget.revenue")}</H3>
                {bg.revenue_streams.map((r, i) => (
                  <div key={i} className="text-sm py-1 border-b border-[#F0EFE9] flex justify-between">
                    <span>{r.name}</span>
                    <span className="font-mono">€{Number(r.monthly_amount).toLocaleString()}/mo · +{r.growth_rate}%</span>
                  </div>
                ))}
              </>
            )}

            <H3>{t("fin.breakeven")}</H3>
            <div className="grid grid-cols-3 gap-3 text-sm">
              <KV k={t("fin.contribution_margin")} v={`${fmt(be.contributionMargin)} (${be.contributionMarginPct.toFixed(1)}%)`} />
              <KV k={t("fin.customers_per_month")} v={be.customersPerMonth != null ? Math.ceil(be.customersPerMonth) : "—"} />
              <KV k={t("fin.month_breakeven")} v={be.breakEvenMonth != null ? `M${be.breakEvenMonth}` : (lang === "es" ? "> 12 meses" : "> 12 months")} />
            </div>

            <H3>{t("fin.pl")} ({lang === "es" ? "Año 1" : "Year 1"})</H3>
            <table className="w-full text-sm border-collapse">
              <tbody>
                <tr className="border-b-2 border-[#1A1A1A]">
                  <td className="py-2 uppercase text-xs tracking-wide">{t("fin.revenue")}</td>
                  <td className="py-2 text-right font-mono">{fmt(pl.revenue)}</td>
                </tr>
                <tr className="border-b border-[#F0EFE9]"><td className="py-1 pl-4 text-[#5C5B5A]">− {t("fin.cogs")}</td><td className="py-1 text-right font-mono">{fmt(pl.cogs)}</td></tr>
                <tr className="border-b-2 border-[#2563EB]"><td className="py-2 uppercase text-xs tracking-wide text-[#2563EB]">= {t("fin.gross_profit")} ({pl.grossMarginPct.toFixed(1)}%)</td><td className="py-2 text-right font-mono text-[#2563EB]">{fmt(pl.grossProfit)}</td></tr>
                <tr className="border-b border-[#F0EFE9]"><td className="py-1 pl-4 text-[#5C5B5A]">− {t("fin.opex")}</td><td className="py-1 text-right font-mono">{fmt(pl.opex)}</td></tr>
                <tr className="border-b border-[#F0EFE9]"><td className="py-1 pl-4 text-[#5C5B5A]">− {t("fin.tax")} ({taxRate}%)</td><td className="py-1 text-right font-mono">{fmt(pl.tax)}</td></tr>
                <tr className={`border-b-4 ${pl.netIncome >= 0 ? "border-[#2563EB]" : "border-[#9E2A2B]"}`}>
                  <td className="py-2 uppercase text-xs tracking-wide font-semibold">= {t("fin.net_income")} ({pl.netMarginPct.toFixed(1)}%)</td>
                  <td className={`py-2 text-right font-mono font-semibold ${pl.netIncome >= 0 ? "text-[#2563EB]" : "text-[#9E2A2B]"}`}>{fmt(pl.netIncome)}</td>
                </tr>
              </tbody>
            </table>

            <H3>{t("fin.balance")}</H3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-xs uppercase tracking-wide text-[#8A8886] mb-1">{t("fin.assets")}</div>
                <KV k={t("fin.cash")} v={fmt(bal.cashEoy)} />
                <KV k={t("fin.fixed_assets")} v={fmt(bal.fixedAssets)} />
                <div className="border-t-2 border-[#1A1A1A] mt-1 py-1 flex justify-between font-mono"><span>{t("fin.total_assets")}</span><span>{fmt(bal.totalAssets)}</span></div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-[#8A8886] mb-1">{t("fin.liabilities")} + {t("fin.equity")}</div>
                <KV k={t("fin.liabilities")} v={fmt(bal.totalLiabilities)} />
                <KV k={t("fin.paid_in")} v={fmt(bal.paidInCapital)} />
                <KV k={t("fin.retained")} v={fmt(bal.retainedEarnings)} />
                <div className="border-t-2 border-[#1A1A1A] mt-1 py-1 flex justify-between font-mono"><span>{t("fin.total_liab_eq")}</span><span>{fmt(bal.totalLiabilities + bal.totalEquity)}</span></div>
              </div>
            </div>

            <H3>{t("fin.five_year")}</H3>
            <table className="w-full text-sm border-collapse font-mono">
              <thead>
                <tr className="border-b-2 border-[#1A1A1A] text-left">
                  <th className="py-2 text-xs uppercase">{t("fin.year")}</th>
                  <th className="py-2 text-xs uppercase text-right">{t("fin.revenue")}</th>
                  <th className="py-2 text-xs uppercase text-right">{t("fin.opex")}</th>
                  <th className="py-2 text-xs uppercase text-right">{t("fin.net_income")}</th>
                  <th className="py-2 text-xs uppercase text-right">{t("fin.cumulative_net")}</th>
                </tr>
              </thead>
              <tbody>
                {fy.map((r) => (
                  <tr key={r.yearIdx} className="border-b border-[#F0EFE9]">
                    <td className="py-1">{r.year}</td>
                    <td className="py-1 text-right">{fmt(r.revenue)}</td>
                    <td className="py-1 text-right">{fmt(r.expenses)}</td>
                    <td className={`py-1 text-right font-semibold ${r.net >= 0 ? "text-[#2563EB]" : "text-[#9E2A2B]"}`}>{fmt(r.net)}</td>
                    <td className={`py-1 text-right font-semibold ${r.cumulative >= 0 ? "text-[#2563EB]" : "text-[#9E2A2B]"}`}>{fmt(r.cumulative)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        );
      })()}

      {/* 10 — Risks */}
      <H2 num="10">{t("module.risks")}</H2>
      {(risks.items || []).length > 0 ? (
        risks.items.map((r, i) => (
          <div key={i} className="mt-3 border-l-2 border-[#B89154] pl-4">
            <div className="font-semibold text-sm">{r.title}</div>
            <div className="text-xs text-[#8A8886] mt-1">
              {t("risk.probability")}: <span className="text-[#1A1A1A]">{r.probability}</span>
              <span className="mx-2">·</span>
              {t("risk.impact")}: <span className="text-[#1A1A1A]">{r.impact}</span>
            </div>
            <P muted>{r.description}</P>
            <div className="text-xs text-[#2563EB] mt-1">→ {r.mitigation}</div>
          </div>
        ))
      ) : (
        <P muted>—</P>
      )}

      {/* 11 — GYA specialized */}
      {(gya.segments || []).length > 0 && (
        <>
          <H2 num="11">{t("module.gya")}</H2>
          <H3>{t("gya.segments_title")}</H3>
          <div className="grid grid-cols-2 gap-3 mt-2 text-xs">
            {gya.segments.map((s) => (
              <div key={s.key} className="border border-[#E8E6E1] p-3">
                <div className="font-semibold text-sm">{s.name}</div>
                {s.tagline && <div className="text-[#5C5B5A] italic mt-1">{s.tagline}</div>}
                {s.kpis && (
                  <div className="mt-2 font-mono text-[10px] text-[#5C5B5A]">
                    Initial €{(s.kpis.avg_ticket_initial || 0).toLocaleString()} · LTV3y €{(s.kpis.ltv_3yr || 0).toLocaleString()} · LTV/CAC {(s.kpis.ltv_cac_ratio || 0).toFixed(1)}× · Payback {s.kpis.payback_months}mo
                  </div>
                )}
              </div>
            ))}
          </div>

          <H3>{t("gya.partners_title")}</H3>
          {Object.entries(gya.country_partners || {}).map(([code, list]) => (
            <div key={code} className="mt-2 mb-3">
              <div className="text-xs font-semibold text-[#1A1A1A] mb-1">
                {code === "ES" ? "🇪🇸 España" : code === "PT" ? "🇵🇹 Portugal" : code === "IT" ? "🇮🇹 Italia" : code} — {list.length} partners
              </div>
              <div className="text-[10px] text-[#5C5B5A]">
                {list.map((p) => `${p.category}: ${p.name}`).join(" · ")}
              </div>
            </div>
          ))}

          <H3>{t("gya.destinations_title")}</H3>
          <div className="grid grid-cols-3 gap-2 text-[10px]">
            {(gya.destinations || []).map((d) => (
              <div key={d.country_code} className="border border-[#E8E6E1] p-3">
                <div className="text-sm font-semibold mb-1">{d.flag} {d.country}</div>
                <div className="text-[#5C5B5A]">CoL idx: {d.cost_of_living_idx} · {d.healthcare}</div>
                <div className="text-[#2563EB] mt-1">{d.best_for_retirees}</div>
                <div className="text-[#B89154]">{d.best_for_families}</div>
              </div>
            ))}
          </div>

          <H3>{t("gya.funnel_title")}</H3>
          {gya.funnel_summary && (
            <div className="grid grid-cols-4 gap-2 text-[10px] font-mono mt-2">
              <KV k="Leads/mo" v={(gya.funnel_summary.monthly_leads_total || 0).toLocaleString()} />
              <KV k="Contracts/mo" v={(gya.funnel_summary.monthly_contracts || 0).toLocaleString()} />
              <KV k="Blended LTV" v={`€${(gya.funnel_summary.blended_ltv || 0).toLocaleString()}`} />
              <KV k="LTV/CAC" v={`${(gya.funnel_summary.blended_ltv_cac || 0).toFixed(1)}×`} />
            </div>
          )}
        </>
      )}

      <div className="mt-16 pt-6 border-t border-[#E8E6E1] text-center text-xs text-[#8A8886] tracking-[0.2em] uppercase font-mono" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
        — End of Business Plan —
      </div>
    </div>
  );
}
